/**
 * VRG-788 — which STANDARD certificate fields a brand may hide, and which it may not.
 *
 * Sibling of VRG-104 (`custom_fields`), which governs the brand's OWN fields. This
 * governs the ones already on every certificate.
 *
 * ## The rule: three actors, three disjoint sets
 *
 * | Controls | Fields |
 * |---|---|
 * | **Vera** | product name, certificate id, status |
 * | **The brand** | SKU, manufacture date, description (+ future product columns) |
 * | **The owner** | owner identity, owned-since, provenance, activity (`certificate_views.visibility`) |
 *
 * The disjointness is load-bearing. If the brand also held a toggle over ownership or
 * provenance, two actors would have authority over one field and a precedence rule would
 * be needed — most-restrictive-wins? brand-wins? — which is unexplainable in a UI and
 * which nobody would remember the direction of. Disjoint sets mean the question never
 * arises.
 *
 * ## Why these lists, and not a single configurable set
 *
 * The locked three are what make the page a certificate rather than a product page, so a
 * brand that can remove them can turn it into neither.
 *
 * ## ⚠️ The Vera verification mark is NOT in either list, and that is the point
 *
 * It was a fourth locked row until 2026-10-31, and listing it was a category error
 * Spencer caught on sight: *"it's not a field — it's implied."*
 *
 * Every other entry here names a piece of DATA about the item, rendered as a labelled
 * row a brand could coherently want gone. The mark is the page's own chrome — the thing
 * saying "this is a Vera record" — and it is not removable by anybody, which means it is
 * not a visibility decision at all. Putting it in a list headed "which of these appear"
 * invited a reader to think the answer could ever be no, and spent a row of a short list
 * reassuring them it could not. A control whose only possible state is its current one
 * is not a control; it is a label pretending to be one.
 *
 * The anti-counterfeiting argument that justified locking it still holds and is simply
 * not expressed here any more: if the thing identifying a page as a Vera-verified record
 * could be removed, a counterfeit page and a legitimate one would look identical, and
 * the recognizability of the layout is itself the fraud signal this product sells. That
 * is a reason the mark is not configurable ANYWHERE, so it belongs with the mark's own
 * rendering, not in a per-SKU settings list.
 *
 * `vera_mark` was display-only — three string literals in this file and nothing reading
 * it (verified by grep across both apps before removing it), so there is no stored value
 * to migrate and nothing to normalize away.
 */

/** Hideable per SKU. Absence from the stored list means SHOWN — hiding is explicit. */
export const CERTIFICATE_HIDEABLE_FIELDS = [
  'sku',
  'manufactured_at',
  'description',
] as const;

export type CertificateHideableField = (typeof CERTIFICATE_HIDEABLE_FIELDS)[number];

/** Never hideable. Rendered in the curation UI as locked, not omitted from it (§46). */
export const CERTIFICATE_LOCKED_FIELDS = [
  'product_name',
  'certificate_id',
  'status',
] as const;

export type CertificateLockedField = (typeof CERTIFICATE_LOCKED_FIELDS)[number];

export type CertificateStandardField =
  | CertificateHideableField
  | CertificateLockedField;

/** Short labels — the curation list's first column. Kept short for the same reason §25 keeps permission labels short. */
export const CERTIFICATE_FIELD_LABELS: Record<CertificateStandardField, string> = {
  product_name: 'Product name',
  certificate_id: 'Certificate ID',
  status: 'Status',
  sku: 'Product ID',
  manufactured_at: 'Manufacture date',
  description: 'Description',
};

/**
 * Why a locked field is locked, shown as the disabled row's tooltip.
 *
 * §46: a disabled control MUST carry a reason the reader can read. If a sentence cannot
 * be written for a field, it does not belong in this list — it belongs in the hideable
 * one.
 */
export const CERTIFICATE_FIELD_LOCK_REASON: Record<CertificateLockedField, string> = {
  product_name: 'A certificate for an unnamed thing certifies nothing.',
  certificate_id:
    'The handle a dispute, a support conversation or a second scan refers to.',
  status: 'The authenticity claim itself — a certificate has to say whether it holds.',
};

/**
 * The stored list, cleaned up: unknown keys dropped, duplicates removed, locked fields
 * refused.
 *
 * Unknown keys are DROPPED rather than kept, because a key nothing renders is a hide
 * that silently does nothing — the brand ticks it, the field stays on the certificate,
 * and there is nothing anywhere to say why. And a locked field appearing here would be a
 * stored instruction the render deliberately ignores, which is the same failure one level
 * up: better to refuse it at the gate than to carry a lie.
 */
export function normalizeHiddenCertificateFields(
  input: unknown,
): CertificateHideableField[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: CertificateHideableField[] = [];
  for (const raw of input) {
    if (typeof raw !== 'string' || seen.has(raw)) continue;
    seen.add(raw);
    if ((CERTIFICATE_HIDEABLE_FIELDS as readonly string[]).includes(raw)) {
      out.push(raw as CertificateHideableField);
    }
  }
  return out;
}

/**
 * Is this field hidden on the certificate?
 *
 * Takes the RAW stored value (`unknown`) so a render site can pass
 * `certificate.product.hidden_certificate_fields` straight in: the payload is untyped on
 * the client, and a helper that demanded a clean array would just move the cast to every
 * call site. Anything that is not a list of known keys means "nothing is hidden", which
 * is the safe default — a field shown when it should not be is a brand's presentation
 * complaint; a field hidden because a malformed column was read as a hide-everything
 * instruction would silently empty the certificate.
 */
export function isCertificateFieldHidden(
  hidden: unknown,
  field: CertificateHideableField,
): boolean {
  return normalizeHiddenCertificateFields(hidden).includes(field);
}
